<template>
  <article>
    <activity-stage :customContentContainerStyles="{ height: '100%' }">
      <article slot="content" class="scrolled-secrets">
        <div class="left-side">
          <div class="instructions-scrolled-secrets">
            <audio-toggle-button></audio-toggle-button>
            <span>{{ activityData.instructions.text }}</span>
          </div>
          <div class="scrollable-container focusable" tabindex="0">
            <div id="content">
              <h3 class="round-title">
                {{ currentRound.roundTitle }}
              </h3>
              <img
                :alt="currentRound?.activityImage?.image?.description"
                :src="currentRound?.activityImage?.image?.src"
              />
              <div
                class="description"
                v-for="(paragraph, idx) in currentRound.paragraphs"
                :key="idx"
              >
                <div v-html="paragraph"></div>
                <br />
              </div>
            </div>
          </div>
        </div>
        <div class="right-side">
          <div class="sentences-list">
            <div
              :class="{
                'sentence-ct': true,
                focusable: true,
                'element-selected': selectedAnswer === answer,
                'disabled-sentence': isCurrentlyTyping
              }"
              v-for="answer in randomizedCurrentRoundAnswers"
              :key="answer.answerId"
              @keyup.enter="handleOnDraggableItemEnterEvent(answer, `droppable-${answer.answerId}`)"
              :tabindex="
                (selectedAnswer && selectedAnswer !== answer) ||
                isCurrentlyTyping ||
                options[answer.answerId]?.replaced
                  ? -1
                  : 0
              "
              :aria-pressed="selectedAnswer === answer"
              role="button"
            >
              <div
                v-if="!options[answer.answerId]?.replaced"
                class="sentence-item droppables"
                :id="`droppable-${answer.answerId}`"
              >
                <svg
                  v-html="cardBorder"
                  class="border-style"
                  height="50"
                  role="img"
                  width="160"
                  :class="[
                    {
                      'element-selected':
                        selectedElement &&
                        selectedElement.answerId === `droppable-${answer.answerId}`
                    }
                  ]"
                ></svg>
                <p class="type-line" style="color: #737373">{{ answer.answer }}</p>
              </div>
            </div>
          </div>
          <div class="results-container" v-if="randomizedCurrentRoundQuestions">
            <div
              class="results-row column-row"
              v-for="answer in randomizedCurrentRoundQuestions"
              :key="answer.answerId"
            >
              <div class="sentence-definition">
                <span v-text="answer.label"></span>
              </div>
              <div class="sentence-definition"></div>
              <div class="sentence-placeholder target-slot">
                <svg
                  class="border-style"
                  height="50"
                  width="160"
                  :aria-label="`target spot for ${answer.label}`"
                  :id="`droparea-${answer.answerId}`"
                  v-html="cardBorderGrey"
                  v-if="!options[answer.answerId].replaced"
                  :tabindex="isCurrentlyTyping ? -1 : 0"
                  @keyup.enter="handleOnDroppableItemEnterEvent(answer.answerId, answer)"
                  :class="[
                    {
                      'selectable-answer': !!selectedElement
                    }
                  ]"
                ></svg>

                <svg
                  height="50"
                  width="160"
                  class="border-style dd"
                  v-html="cardBorder"
                  v-if="options[answer.answerId].replaced"
                ></svg>

                <blinking-cursor-text
                  :class-name="'type-line'"
                  :text="options[answer.answerId].answer"
                  :text-step="options[answer.answerId].stepNumber"
                  :round-id="progress.roundIndex"
                  v-if="options[answer.answerId].replaced"
                >
                </blinking-cursor-text>
              </div>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <article :style="{ display: keyboardEnabled ? 'block' : 'none' }">
      <slot name="keyboard"></slot>
    </article>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';
import Draggable from 'gsap/Draggable';
import { random, shuffle } from 'lodash';

import CommonAudio from 'mixins/CommonAudio';
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AudioToggleButton from 'components/buttons/AudioToggleButton';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import cardBorder from 'assets/svg/scrolled-secrets/scroll-secrets-box.svg';
import cardBorderGrey from 'assets/svg/scrolled-secrets/scroll-secrets-box-grey.svg';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper, CommonAudio],

  components: {
    ActivityStage,
    AudioToggleButton,
    BlinkingCursorText
  },

  data() {
    return {
      selectedElement: null,
      selectedAnswer: null,
      isCurrentlyTyping: false,
      cardBorder,
      cardBorderGrey,
      draggables: [],
      options: {},
      steps: [],
      randomizedCurrentRoundAnswers: [],
      randomizedCurrentRoundQuestions: []
    };
  },

  methods: {
    async loadActivity() {
      this.createRounds({});
      await this.loadImages();
    },

    async loadImages() {
      const images = this.activityData.rounds.map(({ activityImage }) => activityImage.image.src);
      this.queueImage(images);
      this.downloadImages();
    },

    handleDrop(context, usingKeyboard) {
      const {
        target,
        vars: { id }
      } = context;
      const dropArea = document.getElementById(`droparea-${id}`);

      if ((usingKeyboard && dropArea) || context?.hitTest(dropArea, '25%')) {
        this.sndBell();

        const contentPanel = document.querySelector('.activity-stage');
        const contentPanelPosition = contentPanel.getBoundingClientRect();

        // get DOM rects for target and destination
        const targetRect = target.getBoundingClientRect();
        const dropAreaRect = dropArea.getBoundingClientRect();

        const tweenStartPos = {
          left: `${targetRect.left - contentPanelPosition.left}px`,
          top: `${targetRect.top - contentPanelPosition.top}px`,
          position: 'absolute'
        };

        const tweenEndPos = {
          left: `${dropAreaRect.left - contentPanelPosition.left - 2}px`,
          top: `${dropAreaRect.top - contentPanelPosition.top - 2}px`,
          position: 'absolute'
        };

        this.setScreenReaderAlert(`Type the phrase - ${target.innerText}`);

        context.kill();
        TweenMax.set(target, { clearProps: 'all' });
        TweenMax.fromTo(
          target,
          0.65,
          { css: tweenStartPos },
          {
            css: tweenEndPos,
            onComplete: () => this.onDragTransitionComplete(id, dropArea, target)
          }
        );

        this.disableDragging();
      } else {
        this.sndError();
        TweenMax.to(target, 0.5, { css: { x: 0, y: 0 } });
      }
    },

    onDragTransitionComplete(answerId) {
      const { answer } = this.options[answerId];

      this.options[answerId].replaced = true;
      this.options[answerId].stepNumber = this.steps.length;
      this.steps = [...this.steps, { answer }];

      this.overwriteRoundSteps({
        steps: this.steps,
        roundId: this.progress.roundIndex,
        stepIndex: this.options[answerId].stepNumber
      });

      this.overwriteProgressText({ currentText: answer });
      this.draggables = this.draggables.filter(item => item.id !== answerId);
    },

    disableDragging() {
      this.isCurrentlyTyping = true;
      this.selectedElement = null;
      this.selectedAnswer = null;
      this.draggables.forEach(item => item[0].disable());
    },

    enableDragging() {
      this.isCurrentlyTyping = false;
      this.draggables.forEach(item => item[0].enable());
    },

    createDraggables() {
      let bounds = null;
      const self = this;
      const { isMobile } = this.userDevice;
      const { draggables } = this.currentRound;

      const createdDraggables = draggables.map(item => {
        const { answerId } = Object.values(this.options).find(option => option.answer === item);

        let dragTimer = 0;
        let hasHitBoundary = false;
        const draggable = Draggable.create(`#droppable-${answerId}`, {
          autoScroll: 1,
          id: answerId,
          cursor: 'grab',
          text: item,
          edgeResistance: isMobile ? 1.1 : 0.65,
          onPress() {
            if (bounds) return;
            // get DOMRect bounding
            bounds = getActivityBoundings();
          },
          onDragStart() {
            TweenMax.set(`#droppable-${answerId}`, {
              cursor: 'grabbing',
              css: { 'z-index': 110 }
            });
          },
          onDrag() {
            if (!isMobile) return;
            // work-around for iOS not firing touchend on tablet
            clearTimeout(dragTimer);
            const hasTouched = hasTouchedBoundings(this.pointerX, this.pointerY, bounds);
            if (hasTouched && !hasHitBoundary) {
              dragTimer = setTimeout(() => {
                hasHitBoundary = true;
                self.handleDrop(this);
              }, 200);
            }
          },
          onDragEnd() {
            clearTimeout(dragTimer);
            hasHitBoundary = false;
            self.handleDrop(this);
          }
        });
        return draggable;
      });

      this.draggables = createdDraggables;
    },

    fadeInContainer() {
      const self = this;

      TweenMax.to('.scrolled-secrets', 1, {
        opacity: 1,
        onComplete() {
          self.createDraggables();
        }
      });
    },

    setOptions() {
      this.currentRound.answers.forEach(option => {
        const answerId = random(1, 10000);
        this.options[answerId] = {
          ...option,
          answerId,
          replaced: false,
          stepNumber: 0
        };
      });

      return this.options;
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.$nextTick(this.fadeInContainer);
      });
    },

    handleCorrectKey(attempt) {
      const { draggables } = this;

      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();
        this.enableDragging();

        if (this.progress.stepIndex === draggables.length) {
          TweenMax.delayedCall(2, this.completeActivity);
        }
      }
    },

    //keyboard accessible methods
    handleOnDraggableItemEnterEvent(answer, elId) {
      const el = document.getElementById(elId);
      this.selectedAnswer = !this.selectedAnswer ? answer : null;
      this.selectedElement = !this.selectedElement ? el : null;
    },
    handleOnDroppableItemEnterEvent(itemId, answer) {
      if (!this.selectedElement) return;

      if (this.selectedAnswer.answer !== answer.answer) {
        const dropArea = document.getElementById(`droparea-${itemId}`);
        if (!dropArea) return;
        this.sndError();
        return;
      }

      this.handleDrop(
        {
          target: this.selectedElement,
          vars: { id: itemId },
          hitTest: () => false,
          kill: () => false
        },
        true
      );
    }
  },

  watch: {
    currentRound(newVal) {
      if (newVal && newVal.answers) {
        const options = this.setOptions();
        this.randomizedCurrentRoundAnswers = shuffle(Object.values(options));
        this.randomizedCurrentRoundQuestions = shuffle(Object.values(options));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.target-slot .type-line {
  text-indent: 0;
  margin-left: 10px;
}

.scrolled-secrets {
  box-sizing: border-box;
  display: flex;
  height: 485px;
  justify-content: space-evenly;
  padding: 10px;
}

.left-side {
  display: flex;
  flex-flow: column;
  height: 100%;
  width: 47%;

  .instructions-scrolled-secrets {
    font-size: 14px;
    font-family: 'hwtfutura-semibold';
    position: relative;

    button {
      left: -7px;
      position: absolute;
      top: -10px;
    }

    span {
      left: 35px;
      position: relative;
    }
  }

  .scrollable-container {
    .description {
      color: #000;
      font-size: 16px;
      font-family: 'hwtfutura';
    }
  }

  .round-title {
    text-align: center;
    font-weight: bold;
    font-size: 20px;
  }

  .scrollable-container {
    border: 2px solid black;
    width: 100%;
    flex: 1;
    overflow-y: scroll;

    #content {
      padding: 10px;
    }

    img {
      width: 100%;
    }
  }
}

.right-side {
  display: flex;
  flex-flow: column;
  vertical-align: top;
  padding-left: 20px;
  height: 100%;
  width: 50%;

  .sentences-list {
    .sentence-ct {
      width: 160px;
      height: 50px;
      display: inline-block;
      vertical-align: top;
      margin: 5px;

      .sentence-item {
        height: 50px;
        line-height: 50px;
        z-index: 150;

        .border-style {
          z-index: -1;
          position: absolute;
          left: 0;
        }
      }

      .droppables {
        z-index: 100;
        position: relative;

        p {
          font-size: 20px;
        }
      }

      &.disabled-sentence {
        .droppables {
          cursor: auto !important;
        }
      }
    }
  }

  .results-container {
    border: 2px solid black;
    background-color: #cccccc;
    width: 100%;
    flex: 1;
    overflow-y: auto;
    margin-left: 10px;

    .results-row {
      display: flex;
      flex-direction: row;
    }

    .column-title {
      font-family: 'hwtfutura-semibold';
      font-size: 18px;
      line-height: 40px;

      .sentence-placeholder {
        height: 10px;
        line-height: 10px;
      }
    }

    .column-row {
      font-family: 'hwtfutura-semibold';
      font-size: 16px;
      line-height: 50px;
    }

    .sentence-definition {
      display: inline-block;
      flex-grow: 1;
      text-align: center;
      vertical-align: top;
      color: black;

      span {
        white-space: nowrap;
      }
    }

    .sentence-definition-right {
      display: inline-block;
      flex-grow: 1;
      text-align: center;
      vertical-align: top;
      color: black;
    }

    .sentence-placeholder {
      display: inline-block;
      width: 200px;
      height: 50px;
      line-height: 50px;
      margin: 2px;
      position: relative;
    }
  }

  .droppables,
  .noselect,
  svg {
    cursor: pointer;

    &:focus-visible {
      border: 1px solid blue;
    }
  }
}

.type-line {
  font-size: 20px;
  height: 30px;
  left: 0;
  line-height: 30px;
  position: absolute;
  text-indent: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.sentence-item {
  transform: translate3d(0px, 0px, 0px);
  cursor: move;
  touch-action: none;
  user-select: none;
}

.left-side,
.right-side {
  box-sizing: border-box;
}

.scrolled-secrets {
  opacity: 0;
}

.sentence-item {
  width: 160px;
}

.chrome-os {
  .scrolled-secrets {
    height: 400px;
  }
}

.tablet {
  .scrolled-secrets {
    height: 100%;
  }
}
</style>
